import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { callEdgeFunction } from '../config/api'

// Tier types
export type SubscriptionTier = 'free' | 'basic' | 'standard' | 'premium'

/**
 * Tier configuration for UI display
 *
 * IMPORTANT: Core limit values (notesPerMonth, limitSeconds, storageMB)
 * come from the backend API (server/config/tierConfig.js).
 *
 * This local config contains ONLY frontend-specific display values
 * (subtitles, features, pricing, button styles) that don't need to be
 * in the backend.
 *
 * The backend sends tier limits in the subscription status response.
 */
export const TIER_CONFIG = {
  free: {
    label: 'Free',
    subtitle: 'The Taste Test',
    limitSeconds: 100 * 60,          // 100 minutes total (100 notes * 1 min each)
    maxDurationSeconds: 60,          // 1 minute per note
    notesPerMonth: 100,              // Must match backend DEMO_LIMITS.memosPerMonth
    historyDays: null,               // Local only
    cloudBackup: false,
    color: 'gray',
    icon: 'Mic',
    price: null,
    priceMonthly: 0,
    period: 'Forever',
    features: [
      { icon: 'FileText', key: 'subscription.features.notesPerMonth', params: { count: 100 } },
      { icon: 'Clock', key: 'subscription.features.durationLimit', params: { minutes: 1 } },
      { icon: 'HardDrive', key: 'subscription.features.storedLocally' },
      { icon: 'CloudOff', key: 'subscription.features.noCloudBackup' }
    ],
    buttonText: 'Get Started Free',
    buttonStyle: 'muted',
    highlight: false
  },
  basic: {
    label: 'Spark',
    subtitle: 'Personal',
    limitSeconds: 60 * 60,           // 60 minutes (1 hour) total per month
    maxDurationSeconds: 3 * 60,      // 3 minutes per note
    notesPerMonth: 50,
    historyDays: 7,
    cloudBackup: true,
    color: 'teal',
    icon: 'Zap',
    price: '€2.99',
    priceMonthly: 2.99,
    period: '/ mo',
    features: [
      { icon: 'FileText', key: 'subscription.features.notesPerMonth', params: { count: 50 } },
      { icon: 'Clock', key: 'subscription.features.durationLimit', params: { minutes: 3 } },
      { icon: 'History', key: 'subscription.features.historyDays', params: { days: 7 } },
      { icon: 'FileAudio', key: 'subscription.features.basicTranscription' }
    ],
    buttonText: 'Choose Spark',
    buttonStyle: 'outlined',
    highlight: false
  },
  standard: {
    label: 'Memory',
    subtitle: 'Most Popular',
    limitSeconds: 300 * 60,          // 300 minutes (5 hours) total per month
    maxDurationSeconds: 20 * 60,     // 20 minutes per note
    notesPerMonth: null,             // Unlimited
    historyDays: null,               // Forever
    cloudBackup: true,
    color: 'purple',
    icon: 'Star',
    price: '€6.99',
    priceMonthly: 6.99,
    period: '/ mo',
    features: [
      { icon: 'Infinity', key: 'subscription.features.unlimitedNotes' },
      { icon: 'CloudCheck', key: 'subscription.features.foreverHistory' },
      { icon: 'Sparkles', key: 'subscription.features.smartTagging' },
      { icon: 'Download', key: 'subscription.features.advancedExport' },
      { icon: 'Clock', key: 'subscription.features.durationLimit', params: { minutes: 20 } }
    ],
    buttonText: 'Get Memory',
    buttonStyle: 'filled',
    highlight: true,
    badge: 'Most Popular'
  },
  premium: {
    label: 'Intel',
    subtitle: 'Power User',
    limitSeconds: 1200 * 60,         // 1200 minutes (20 hours) total per month
    maxDurationSeconds: 2 * 60 * 60, // 2 hours per note
    notesPerMonth: null,             // Unlimited
    historyDays: null,               // Forever
    cloudBackup: true,
    color: 'slate',
    icon: 'Crown',
    price: '€19.99',
    priceMonthly: 19.99,
    period: '/ mo',
    features: [
      { icon: 'Video', key: 'subscription.features.hourDurationLimit', params: { hours: 2 } },
      { icon: 'Brain', key: 'subscription.features.aiSummaries' },
      { icon: 'Star', key: 'subscription.features.priorityProcessing' },
      { icon: 'Users', key: 'subscription.features.meetingMode' }
    ],
    buttonText: 'Choose Intel',
    buttonStyle: 'outlined-dark',
    highlight: false
  }
} as const

export interface SubscriptionStatus {
  tier: SubscriptionTier
  tierLabel: string
  tierColor: string
  tierIcon: string
  // Notes-based usage tracking
  monthlyMemoCount: number
  monthlyNotesLimit: number | null
  notesRemaining: number | null
  usagePercent: number
  canCreateMemo: boolean
  // Legacy audio fields (deprecated but kept for backwards compatibility)
  monthlyAudioSeconds: number
  monthlyLimitSeconds: number
  audioRemainingSeconds: number
  subscriptionStarted: string | null
  subscriptionExpires: string | null
  // Payment source fields
  subscriptionSource: 'stripe' | 'apple' | 'gift' | 'admin' | null
  canManageOnWeb: boolean
  canManageOnIOS: boolean
  hasStripeCustomer: boolean
  // Expiration/cancellation info
  isExpired: boolean
  cancelAtPeriodEnd: boolean
  storedTier: SubscriptionTier
  storedTierLabel: string
  tiers: typeof TIER_CONFIG
}

export const useSubscriptionStore = defineStore('subscription', () => {
  const status = ref<SubscriptionStatus | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Request deduplication - prevent multiple simultaneous fetches
  let pendingFetch: Promise<boolean> | null = null

  // Computed properties
  const tier = computed<SubscriptionTier>(() => status.value?.tier ?? 'free')
  const tierLabel = computed(() => TIER_CONFIG[tier.value].label)
  const tierColor = computed(() => TIER_CONFIG[tier.value].color)
  const tierIcon = computed(() => TIER_CONFIG[tier.value].icon)

  const isPremium = computed(() => status.value?.tier === 'premium')
  const isPaid = computed(() => (status.value ? status.value.tier !== 'free' : false))
  const isFree = computed(() => (status.value ? status.value.tier === 'free' : true))
  const canCreateMemo = computed(() => status.value?.canCreateMemo ?? true)

  // Notes-based usage tracking
  const monthlyMemoCount = computed(() => status.value?.monthlyMemoCount ?? 0)
  // Note: monthlyNotesLimit can be null for unlimited tiers - don't use ?? which treats null as falsy
  // Only use fallback when status itself is not loaded yet
  const monthlyNotesLimit = computed(() => status.value ? status.value.monthlyNotesLimit : TIER_CONFIG.free.notesPerMonth)
  const notesRemaining = computed(() => status.value ? status.value.notesRemaining : TIER_CONFIG.free.notesPerMonth)
  const usagePercent = computed(() => status.value?.usagePercent ?? 0)

  // Formatted values for notes
  const usageFormatted = computed(() => `${monthlyMemoCount.value}`)
  const limitFormatted = computed(() => monthlyNotesLimit.value === null ? '∞' : `${monthlyNotesLimit.value}`)
  const remainingFormatted = computed(() => notesRemaining.value === null ? '∞' : `${notesRemaining.value}`)

  // Legacy audio computed (deprecated)
  const monthlyAudioSeconds = computed(() => status.value?.monthlyAudioSeconds ?? 0)
  const monthlyLimitSeconds = computed(() => status.value?.monthlyLimitSeconds ?? 0)
  const audioRemainingSeconds = computed(() => status.value?.audioRemainingSeconds ?? 0)

  // Helper to format seconds as "Xm" or "Xh Ym" (kept for compatibility)
  function formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) {
      return `${minutes}m`
    }
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    if (remainingMinutes === 0) {
      return `${hours}h`
    }
    return `${hours}h ${remainingMinutes}m`
  }

  async function fetchStatus(): Promise<boolean> {
    // Deduplicate concurrent requests - return existing promise if one is in flight
    if (pendingFetch) {
      return pendingFetch
    }

    loading.value = true
    error.value = null

    pendingFetch = (async () => {
      try {
        console.log('[subscription] Fetching status via Edge Function')
        const data = await callEdgeFunction<SubscriptionStatus>('subscription-status', {
          method: 'GET'
        })
        status.value = data
        return true
      } catch (e) {
        console.error('Error fetching subscription status:', e)
        error.value = e instanceof Error ? e.message : 'Unknown error'
        return false
      } finally {
        loading.value = false
        pendingFetch = null
      }
    })()

    return pendingFetch
  }

  function reset() {
    status.value = null
    error.value = null
  }

  // Update usage after memo creation (increments notes count)
  function addUsage() {
    if (status.value) {
      status.value.monthlyMemoCount += 1
      const limit = status.value.monthlyNotesLimit
      if (limit !== null) {
        status.value.notesRemaining = Math.max(0, limit - status.value.monthlyMemoCount)
        status.value.usagePercent = Math.min(100, Math.round((status.value.monthlyMemoCount / limit) * 100))
        status.value.canCreateMemo = status.value.notesRemaining > 0
      }
      // Unlimited tiers always can create
    }
  }

  // Subscription source computed properties
  const subscriptionSource = computed(() => status.value?.subscriptionSource ?? null)
  const canManageOnWeb = computed(() => status.value?.canManageOnWeb ?? false)
  const canManageOnIOS = computed(() => status.value?.canManageOnIOS ?? false)
  const hasStripeCustomer = computed(() => status.value?.hasStripeCustomer ?? false)

  // Expiration/cancellation info computed properties
  const isExpired = computed(() => status.value?.isExpired ?? false)
  const cancelAtPeriodEnd = computed(() => status.value?.cancelAtPeriodEnd ?? false)
  const storedTier = computed<SubscriptionTier>(() => status.value?.storedTier ?? 'free')
  const storedTierLabel = computed(() => TIER_CONFIG[storedTier.value].label)

  // Create Stripe checkout session
  async function createCheckout(tier: SubscriptionTier): Promise<{ url: string } | { error: string }> {
    try {
      const data = await callEdgeFunction<{ url: string }>('stripe-create-checkout', {
        body: { tier }
      })
      return { url: data.url }
    } catch (e) {
      console.error('Error creating checkout session:', e)
      return { error: e instanceof Error ? e.message : 'Unknown error' }
    }
  }

  // Open Stripe customer portal for managing subscription
  async function openCustomerPortal(): Promise<{ url: string } | { error: string }> {
    try {
      const data = await callEdgeFunction<{ url: string }>('stripe-portal', {
        body: {}
      })
      return { url: data.url }
    } catch (e) {
      console.error('Error opening customer portal:', e)
      return { error: e instanceof Error ? e.message : 'Unknown error' }
    }
  }

  // Sync Stripe subscription after portal changes
  async function syncStripeSubscription(): Promise<boolean> {
    try {
      await callEdgeFunction('stripe-sync', { body: {} })
      return true
    } catch (e) {
      console.error('Error syncing Stripe subscription:', e)
      return false
    }
  }

  return {
    status,
    loading,
    error,
    // Tier info
    tier,
    tierLabel,
    tierColor,
    tierIcon,
    // Tier checks
    isPremium,
    isPaid,
    isFree,
    canCreateMemo,
    // Notes-based usage data
    monthlyMemoCount,
    monthlyNotesLimit,
    notesRemaining,
    usagePercent,
    // Legacy audio data (deprecated)
    monthlyAudioSeconds,
    monthlyLimitSeconds,
    audioRemainingSeconds,
    // Formatted values
    usageFormatted,
    limitFormatted,
    remainingFormatted,
    formatDuration,
    // Actions
    fetchStatus,
    reset,
    addUsage,
    // Stripe integration
    subscriptionSource,
    canManageOnWeb,
    canManageOnIOS,
    hasStripeCustomer,
    createCheckout,
    openCustomerPortal,
    syncStripeSubscription,
    // Expiration/cancellation info
    isExpired,
    cancelAtPeriodEnd,
    storedTier,
    storedTierLabel,
    // Config
    TIER_CONFIG
  }
})
